import { isToday } from 'date-fns'
import { useState } from 'react'
import { useSchedulerSummary } from '@/api/queries'
import { Button } from '@/components/ui/Button'
import { ErrorState } from '@/components/ui/EmptyState'
import { Icons } from '@/components/ui/Icons'
import { Widget } from '@/components/ui/Widget'
import { ago, dateTime, toDate } from '@/lib/format'
import { ScheduledJobsDialog } from './ScheduledJobsDialog'
import { formatPeriod, isSendLate } from './schedulerSummary'
import styles from './scheduler.module.css'

/** Time of day for today's runs; older ones need the date to stand out as stale. */
function sentTime(value: string): string {
  return dateTime(value, isToday(toDate(value)) ? 'HH:mm' : 'yyyy-MM-dd HH:mm')
}

/** Jobs whose latest run failed, and when the scheduler last sent one; late past the most frequent job's period. */
export function SchedulersWidget({ className }: { className?: string }) {
  const { data, isLoading, error } = useSchedulerSummary()
  const [open, setOpen] = useState<{ scheduler?: string } | null>(null)
  const failed = data?.failedJobs ?? []
  const late = data ? isSendLate(data) : false
  const period = data?.shortestPeriodMinutes != null ? `Expected every ${formatPeriod(data.shortestPeriodMinutes)}` : undefined
  const expected = period && `${period} (${data!.shortestPeriodJob})`

  const lastSent = data?.lastSentAt ? (
    <span title={data.lastSentJob ?? undefined}>
      <time dateTime={data.lastSentAt}>{sentTime(data.lastSentAt)}</time> <span className={styles.muted}>· {ago(data.lastSentAt)}</span>
    </span>
  ) : (
    'None in 7 days'
  )

  return (
    <Widget
      title="Scheduled jobs"
      onTitleClick={() => setOpen({})}
      loading={isLoading}
      className={className}
      actions={
        <Button size="sm" onClick={() => setOpen({})}>
          Manage
        </Button>
      }
    >
      {error ? (
        <ErrorState error={error} />
      ) : !data ? null : failed.length === 0 && !late ? (
        <div className={styles.summary} title={expected}>
          <p className={styles.noIssues}>No issues</p>
          <p className={styles.muted}>Last job sent {lastSent}</p>
        </div>
      ) : (
        <>
          <div className={styles.tiles}>
            <div className={`${styles.tile} ${failed.length > 0 ? styles.tileDanger : ''}`}>
              <div className={styles.tileLabel}>Failed jobs</div>
              <div className={styles.tileValue}>
                {failed.length > 0 && <Icons.Alert size={14} className={styles.dangerIcon} />} {failed.length}
              </div>
            </div>
            <div className={`${styles.tile} ${late ? styles.tileWarn : ''}`} title={expected}>
              <div className={styles.tileLabel}>Last job sent{late && ' · late'}</div>
              <div className={styles.tileValue}>
                {late && <Icons.Alert size={14} className={styles.warnIcon} />} {lastSent}
              </div>
              {late && period && <div className={styles.tileNote}>{period}</div>}
            </div>
          </div>
          <ul className={styles.failedList}>
            {failed.map((job) => (
              <li key={job.id}>
                <button type="button" className={styles.failedJob} onClick={() => setOpen({ scheduler: job.schedulerName })} title={job.message ?? undefined}>
                  <span className={styles.failedName}>{job.name}</span>
                  <span className={styles.muted}>
                    {job.schedulerName} · {ago(job.failedAt)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      {open && <ScheduledJobsDialog initialScheduler={open.scheduler} onClose={() => setOpen(null)} />}
    </Widget>
  )
}
